import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import jp from 'jsonpath';
import { TriangleIcon } from '../../../assets/icons';

import { AutoComplete, Typography, IconButton } from '../../components';
import DatePicker from './DatePicker';
import RightArrowIcon from '../../../assets/icons/RightArrowIcon';

const useStyles = makeStyles((theme) => ({
  selectorBox: {
    width: '100%',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '50px'
  },
  selectorYears: {
    display: 'flex',
    flexDirection: 'row-reverse',
    width: '30%',
  },
  selectorMonths: {
    width: '25%',
    height: '50px',
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down(960)]: {
      width: '30%',
    },
    [theme.breakpoints.down(820)]: {
      width: '40%',
    },
  },
  leftArrow: {
    transform: 'rotateY(180deg)',
  },
  calendarContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '50px',
    margin: 'auto',
    [theme.breakpoints.down(1150)]: {
      flexDirection: 'column',
    },
  },
  arrowButton: {
    width: '40px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0px',
    marginBottom: '5px'
  },
  legend: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    margin: 'auto',
    paddingTop: '50px',
    [theme.breakpoints.down(1150)]: {
      flexDirection: 'column',
    },
  },
  triangle: {
    marginRight: '1px',
    marginLeft: '1px',
    transform: 'rotateX(180deg)',
  },
  legendItem: {
    minHeight: '70px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: '10px',
    width: '16.6%',
    [theme.breakpoints.down(1150)]: {
      width: '100% !important',
      margin: '0px !important',
      marginTop: '15px !important',
      paddingLeft: '20px',
      minHeight: '30px',
    },
  }
}));

const month = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

const Main = ({
  intl,
  data,
  years,
}) => {
  const classes = useStyles();
  const [year, setYear] = useState({
    name: moment().format('YYYY'),
    id: moment().format('YYYY')
  });
  const [legends, setLegends] = useState([]);
  const [isSecondHalfOfTheYear, setIsSecondHalfOfTheYear] = useState(false);
  const firstHalfYear = !isSecondHalfOfTheYear ? month.slice(0, 3) : month.slice(6, 9);
  const secondHalfYear = !isSecondHalfOfTheYear ? month.slice(3, 6) : month.slice(9, 12);
  const thisYear = data.filter((item) => String(item.year) === year.name)[0];

  useEffect(() => {
    if (thisYear) {
      const collections = jp.query(thisYear, '$..collections');
      let allCollections = [];
      collections.map((item) => {
        allCollections = allCollections.concat(item);
        return null;
      });
      const nodes = allCollections.map(
        (item) => (JSON.stringify({
          icon: item.icon,
          title: item.title,
          color: item.color,
          shape: item.shape
        }))
      );
      const uniq = [...new Set(nodes)].map((item) => JSON.parse(item));
      setLegends(uniq);
    }
  }, [thisYear]);

  return (
    <>
      <div className={classes.selectorBox}>
        <div className={classes.selectorYears}>
          <AutoComplete
            className="w-100"
            title={intl.formatMessage({ id: 'GENERAL.YEAR' })}
            options={years}
            value={year}
            onChange={(data) => {
              setYear(data);
              setIsSecondHalfOfTheYear(false);
            }}
            optionTitle="name"
            optionId="id"
          />
        </div>
        <div className={classes.selectorMonths}>

          <IconButton
            className={classes.arrowButton}
            onClick={() => setIsSecondHalfOfTheYear(true)}
          >
            <RightArrowIcon
              color={isSecondHalfOfTheYear
                ? process.env.REACT_APP_COLOR_DISABLED_TEXT
                : process.env.REACT_APP_COLOR_TEXT}
            />
          </IconButton>
          <Typography className="d-flex h-100 align-items-center justify-content-center text-center" variant="body">
            {!isSecondHalfOfTheYear
              ? intl.formatMessage({ id: 'SCHEDULE.SELECT_BOX.FIRST_HALF_YEAR_LABEL' })
              : intl.formatMessage({ id: 'SCHEDULE.SELECT_BOX.SECOND_HALF_YEAR_LABEL' })}
            {' '}
            {year.name}
          </Typography>
          <IconButton
            className={classes.arrowButton}
            onClick={() => setIsSecondHalfOfTheYear(false)}
          >
            <RightArrowIcon
              className={classes.leftArrow}
              color={!isSecondHalfOfTheYear
                ? process.env.REACT_APP_COLOR_DISABLED_TEXT
                : process.env.REACT_APP_COLOR_TEXT}
            />
          </IconButton>

        </div>
      </div>
      <div className={`${classes.calendarContainer}`}>
        {firstHalfYear.map((item) => (
          <DatePicker
            key={`first_half_of_year--${item}`}
            value={moment(`${year.id}-${item}-01`)}
            month={thisYear && thisYear.months.filter((it) => it.month === Number(item))
              ? thisYear.months.filter((it) => it.month === Number(item))[0] : []}
          />
        ))}
      </div>
      <div className={`${classes.calendarContainer}`}>
        {secondHalfYear.map((item) => (
          <DatePicker
            key={`second_half_of_year--${item}`}
            value={moment(`${year.id}-${item}-01`)}
            month={thisYear && thisYear.months.filter((it) => it.month === Number(item))
              ? thisYear.months.filter((it) => it.month === Number(item))[0] : []}
          />
        ))}
      </div>
      <div className={`row ${classes.legend}`}>
        {
          legends && legends.length > 0
          && legends.map((item, id) => (
            <div
              id={`legendItem - ${String(id)}`}
              className={`${classes.legendItem}`}
            >
              { item.shape === 'triangle' ? (
                <TriangleIcon
                  color={`#${item.color}`}
                  className={classes.triangle}
                  width="18px"
                  height="18px"
                />
              )
                : (
                  <div style={{
                    minWidth: '15px',
                    minHeight: '15px',
                    borderRadius: item.shape === 'circle' ? '100px' : '2px',
                    backgroundColor: `#${item.color}`,
                    marginTop: '2px'
                  }}
                  />
                )}
              <Typography className="ml-2" variant="bodySemiBold">
                {item.title}
              </Typography>
            </div>
          ))
        }
      </div>
    </>
  );
};

export default injectIntl(Main);
